import type { TExample } from '@atlas/uicorn/preview/modules';

import {
    useCallback,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';

import * as React from 'react';
import styled, { createGlobalStyle, css, keyframes } from 'styled-components';
import useRefCallback from 'use-ref-callback';
import Button from '@atlas/uicorn/lib/Control/Button';
import IconButton from '@atlas/uicorn/lib/Control/IconButton';
import Emoji from '@atlas/uicorn/lib/Data/Emoji';
import Dialog, { type TDialogRef } from '@atlas/uicorn/lib/Display/Dialog';
import Text from '@atlas/uicorn/lib/Display/Text';
import CrossIcon from '@atlas/uicorn/lib/Icon/CrossIcon';
import Spinner from '@atlas/uicorn/lib/Display/Spinner';
import FormItem from '@atlas/uicorn/lib/Control/FormItem';
import Form from '@atlas/uicorn/lib/Control/Form';
import Input from '@atlas/uicorn/lib/Input/Input';
import Select from '@atlas/uicorn/lib/Input/Select';
import UnmountDelay, { type TUnmountDelayRef } from '@atlas/uicorn/lib/Display/UnmountDelay';
import { registerStylesheet } from '@atlas/uicorn/lib/System/StylesProvider';

const Modal = React.memo(
    React.forwardRef(function Modal(props: TModalProps, ref: React.ForwardedRef<TDialogRef>): React.ReactElement {
        const {
            onClose,
            children,
            header,
            footer,
            noScroll,
            noBodyPadding,
            noBackdropClick,
            onBackdropClick,
            onClick,
            onKeyDown,
            ...dialogProps
        } = props;

        const unmountDelayRef = useRef<TUnmountDelayRef>(null);

        const handleCloseCallback = useRefCallback<React.ReactEventHandler<HTMLDialogElement>>((e) => {
            unmountDelayRef.current?.playOnce();
            onClose?.(e);
        });
        const [dialogEl, setDialogEl] = useState<HTMLDialogElement | null>(null);

        // TODO: Probably, need to reset unmountDelayRef if modal is shown using methods
        useImperativeHandle<TModalRef | null, TModalRef | null>(ref, () => dialogEl ?? null, [dialogEl]);

        useLayoutEffect(
            function openDialog() {
                !dialogEl?.open && dialogEl?.showModal();
            },
            [dialogEl],
        );

        // Dialog is dropping autofocus from inputs, so we have to explicitly find it and re-apply focus on mount
        useEffect(
            function persistAutoFocus() {
                dialogEl?.querySelector<HTMLInputElement>('[data-autofocus=true]')?.focus();
            },
            [dialogEl],
        );

        const handleClickOutsideDialog = useRefCallback(function handleClickOutsideDialog(
            event: React.MouseEvent<HTMLDialogElement, MouseEvent>,
        ) {
            if (event.target === event.currentTarget) {
                const dialogDimensions = event.currentTarget.getBoundingClientRect();
                if (
                    event.clientX < dialogDimensions.left ||
                    event.clientX > dialogDimensions.right ||
                    event.clientY < dialogDimensions.top ||
                    event.clientY > dialogDimensions.bottom
                ) {
                    if (noBackdropClick) return;
                    onBackdropClick?.(event);
                    if (!event.defaultPrevented) {
                        onClose?.(event);
                        if (!event.defaultPrevented) {
                            event.currentTarget.close();
                        }
                    }
                }
            }

            onClick?.(event);
        });

        const handleMountClone = useRefCallback((clone: HTMLDialogElement) =>
            Object.assign(clone, { open: false, className: `${clone.className} unmounting` }),
        );

        const handleMountedClone = useRefCallback((clone: HTMLDialogElement) => clone.showModal());

        const handleKeyDown = useRefCallback<React.KeyboardEventHandler<HTMLDialogElement>>((event) => {
            onKeyDown?.(event);
            // TODO: It doesn't prevent all CommandBar callbacks but it does so for table navigation
            event.stopPropagation();
        });

        return (
            <UnmountDelay<HTMLDialogElement>
                delay={unmountTime}
                onMountClone={handleMountClone}
                onMountedClone={handleMountedClone}
                ref={unmountDelayRef}
            >
                <SModalDialog
                    ref={setDialogEl}
                    onMouseDown={handleClickOutsideDialog}
                    onClose={handleCloseCallback}
                    onKeyDown={handleKeyDown}
                    {...dialogProps}
                >
                    {onClose && (
                        <SCloseButton
                            icon={<CrossIcon />}
                            tabIndex={-1}
                            tooltip=""
                            onClick={(e) => {
                                onClose(e);
                                if (e.defaultPrevented) return;
                                e.currentTarget.closest('dialog')?.close();
                            }}
                        />
                    )}
                    {header && (
                        <SModalHeaderContainer>
                            {typeof header === 'string' ? <Text h="3">{header}</Text> : header}
                        </SModalHeaderContainer>
                    )}
                    <SModalBody children={children} $noScroll={!!noScroll} $noBodyPadding={!!noBodyPadding} />
                    {footer && <SModalFooter children={footer} />}
                </SModalDialog>
            </UnmountDelay>
        );
    }),
);

export default Modal;

// #region Helpers

export const unmountTime = 0.2;

// Temporary workaround for the issue with missing focus on 2nd level modal
export const onUnmountCallback = (cb: () => void) => {
    setTimeout(() => {
        cb();
    }, unmountTime * 1e3);
};

// #endregion

// #region Styles

const animateBackdropAppearance = keyframes`
    to {
        // color variables are not available, because it is rendered in #top-layer next to :root
        background-color: rgba(38, 38, 38, 0.64);
    }
`;

const animateModalAppearance = keyframes`
    from {
        opacity: 0;
        translate: 0% -20%;
        scale: 0.8;
    }

    to {
        opacity: 1;
        translate: 0% 0%;
        scale: 1;
    }
`;

const SModalDialog = styled(Dialog).attrs({ elevation: 'medium', modal: true })`
    // TODO: Carefully swap them (double check all usages of the modal)
    --modal-spacing: 1.6rem;
    --modal-close-button-size: 2.4rem;

    padding: 0;

    width: 30vw;
    ${(p) =>
        p.theme.when.screenVariant(
            'compact',
            css`
                width: calc(100vw - 2.4rem);
            `,
        )}
    min-width: 38.0rem;
    max-width: 57.6rem; // 1920px screen / 3
    max-height: 75vh;
    overflow: visible;
    margin-top: 15vh;
    flex-direction: column;

    border: ${(p) => `0.1rem solid ${p.theme.color.grey62}`};
    border-radius: ${(p) => p.theme.radius.medium};

    animation: ${animateModalAppearance} 0.3s ease forwards;

    &::backdrop {
        animation: ${animateBackdropAppearance} 0.3s ease forwards;
    }
`;

const SModalHeaderContainer = styled.header`
    position: sticky;
    top: 0;
    width: 100%;

    padding: var(--modal-spacing);
    &:not(:last-child) {
        padding-bottom: calc(var(--modal-spacing) / 2);
    }
    // TODO: Replace with SCloseButton
    &:not(:first-child) {
        padding-right: calc(var(--modal-close-button-size) + var(--modal-spacing));
    }

    display: flex;
    justify-content: space-between;

    color: ${(p) => p.theme.color.grey9};
`;

export const SModalBody = styled.section<{ $noScroll: boolean; $noBodyPadding: boolean }>`
    overflow: ${(p) => (p.$noScroll ? 'visible' : 'auto')};

    padding: ${(p) => (p.$noBodyPadding ? '0' : `var(--modal-spacing)`)};
    &:not(:last-child) {
        padding-bottom: calc(var(--modal-spacing) / 2);
    }
    ${SModalHeaderContainer} + & {
        padding-top: calc(var(--modal-spacing) / 2);
    }
`;

const SModalFooter = styled.footer`
    position: sticky;
    bottom: 0;
    width: 100%;

    padding: var(--modal-spacing);
    ${SModalHeaderContainer} + &, ${SModalBody} + & {
        padding-top: calc(var(--modal-spacing) / 2);
    }

    display: flex;
    justify-content: flex-end;
    align-items: center;
    column-gap: 1.6rem;

    font: ${(p) => p.theme.font.input};
`;

export const SCloseButton = styled(IconButton)`
    position: absolute;
    top: var(--modal-spacing);
    right: var(--modal-spacing);
    z-index: 5;
`;

const unmountKeyframes = keyframes`
    from {
        opacity: 1;
        translate: 0% 0%;
        scale: 1;
    }
    to {
        opacity: 0;
        translate: 0% -30%;
        scale: 1;
    }
`;

const UnmountModalStylesheet = createGlobalStyle`
    ${SModalDialog}.unmounting {
        animation: ${unmountKeyframes} ${unmountTime}s ease forwards !important;

        &::backdrop {
            animation: none !important;
            opacity: 0 !important;
        }
    }
`;

registerStylesheet(UnmountModalStylesheet);

// #endregion

// #region Types

export type TModalRef = TDialogRef;

export type TModalProps = Omit<React.HTMLAttributes<HTMLDialogElement>, 'title'> & {
    header?: React.ReactNode;
    footer?: React.ReactNode;
    noScroll?: boolean; // TODO: It is currently used to fix visibility of the Select dropdowns, can be dropped when Select is using Portal dialog
    noBackdropClick?: boolean;
    noBodyPadding?: boolean;
    onBackdropClick?: (event: React.MouseEvent<HTMLDialogElement, MouseEvent>) => void;
    onClose?: (
        e:
            | React.MouseEvent<HTMLButtonElement | HTMLDialogElement, MouseEvent>
            | React.SyntheticEvent<HTMLDialogElement, Event>,
    ) => void;
};

// #endregion

// #region Examples

export const FormExample: TExample = {
    render(props) {
        const [isModalOpen, setIsModalOpen] = useState(false);
        const [isSaving, setIsSaving] = useState(false);
        const [form] = Form.useForm();
        const resetValues = useCallback(() => form.resetFields(), [form]);

        return (
            <>
                <Button onClick={() => setIsModalOpen(true)}>Trigger Modal Form</Button>
                {isModalOpen && (
                    <Modal
                        header="Create notification"
                        onClose={() => {
                            setIsModalOpen(false);
                            resetValues();
                        }}
                        footer={
                            <>
                                {isSaving && <Spinner />}
                                <Button disabled={isSaving} type="tertiary" danger onClick={resetValues}>
                                    Clear values
                                </Button>
                                <Button type="primary" onClick={() => form.submit()} disabled={isSaving}>
                                    Save
                                </Button>
                            </>
                        }
                    >
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={(v) => {
                                setIsSaving(true);
                                new Promise((resolve, reject) => {
                                    setTimeout(() => {
                                        try {
                                            resolve(v);
                                        } catch (error) {
                                            reject(error);
                                        }
                                    }, 1e3);
                                }).then((v) => {
                                    props.onFinish(v);
                                    setIsSaving(false);
                                    setIsModalOpen(false);
                                    resetValues();
                                });
                            }}
                        >
                            <Text p color="grey8">
                                Optional description copy. A Dialog is a flexible floating surface, used to display
                                transient content such as menus, selection options, dialogs, and more.
                            </Text>
                            <FormItem label="Notification name" name="name" rules={[{ required: true }]}>
                                <Input placeholder="Enter notification name" />
                            </FormItem>
                            <Text p color="grey8">
                                Optional description copy. A Dialog is a flexible floating surface, used to display
                                transient content such as menus, selection options, dialogs, and more.
                            </Text>
                            <FormItem label="Send via" name="via" rules={[{ required: true }]}>
                                <Select
                                    placeholder="Select channel"
                                    options={[
                                        { key: 'slack', label: 'Slack' },
                                        { key: 'email', label: 'Email' },
                                        { key: 'sms', label: 'SMS' },
                                    ]}
                                />
                            </FormItem>
                        </Form>
                    </Modal>
                )}
            </>
        );
    },
    handlers: {
        onFinish: 'dashboard',
    },
};

export const LayoutExample: TExample = {
    render(props) {
        return (
            <>
                <Modal
                    header={
                        <>
                            <Text h="2">
                                <Emoji emoji="warning" size={16} /> Scrollable modal with a long long Optional
                                description copy
                            </Text>
                        </>
                    }
                    footer={<Button type="primary">Primary action</Button>}
                    onClose={props.onClose}
                >
                    <p>
                        Optional description copy. A Dialog is a flexible floating surface, used to display transient
                        content such as menus, selection options, dialogs, and more.
                    </p>
                    <p>
                        Optional description copy. A Dialog is a flexible floating surface, used to display transient
                        content such as menus, selection options, dialogs, and more.
                    </p>
                    <p>
                        Optional description copy. A Dialog is a flexible floating surface, used to display transient
                        content such as menus, selection options, dialogs, and more.
                    </p>
                    <p>
                        Optional description copy. A Dialog is a flexible floating surface, used to display transient
                        content such as menus, selection options, dialogs, and more.
                    </p>
                    <p>
                        Optional description copy. A Dialog is a flexible floating surface, used to display transient
                        content such as menus, selection options, dialogs, and more.
                    </p>
                    <p>
                        Optional description copy. A Dialog is a flexible floating surface, used to display transient
                        content such as menus, selection options, dialogs, and more.
                    </p>
                    <p>
                        Optional description copy. A Dialog is a flexible floating surface, used to display transient
                        content such as menus, selection options, dialogs, and more.
                    </p>
                    <p>
                        Optional description copy. A Dialog is a flexible floating surface, used to display transient
                        content such as menus, selection options, dialogs, and more.
                    </p>
                    <p>
                        Optional description copy. A Dialog is a flexible floating surface, used to display transient
                        content such as menus, selection options, dialogs, and more.
                    </p>
                    <p>
                        Optional description copy. A Dialog is a flexible floating surface, used to display transient
                        content such as menus, selection options, dialogs, and more.
                    </p>
                    <p>
                        Optional description copy. A Dialog is a flexible floating surface, used to display transient
                        content such as menus, selection options, dialogs, and more.
                    </p>
                </Modal>
                Refresh to open again
            </>
        );
    },
    handlers: {
        onClose: 'console',
    },
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it } = import.meta.vitest;
    describe('Modal', async () => {
        // const testModule = '@test'; const { render } = await import(/* @vite-ignore */ testModule);

        it.todo('matches snapshot');
    });
}

// #endregion
